import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, X, Settings, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * STORAGE NOTE: localStorage is intentionally used here for cookie preferences.
 * This data is non-sensitive (user's cookie consent choices) and needs to persist
 * across browser sessions. localStorage is appropriate for UI preferences that:
 * - Contain no PII or authentication credentials
 * - Need persistence across sessions
 * - Are not security-critical
 */
const COOKIE_CONSENT_KEY = 'ec_cookie_consent';

const CookieConsent = () => {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true,
    analytics: false,
    marketing: false,
    functional: false
  });

  useEffect(() => {
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      const timer = setTimeout(() => setIsVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const saveConsent = (prefs) => {
    const consentData = {
      ...prefs,
      timestamp: new Date().toISOString(),
      version: '1.0'
    };
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consentData));
    setIsVisible(false);
  };

  const acceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true,
      functional: true
    };
    setPreferences(allAccepted);
    saveConsent(allAccepted);
  };

  const acceptNecessary = () => {
    const necessaryOnly = {
      necessary: true,
      analytics: false,
      marketing: false,
      functional: false
    };
    setPreferences(necessaryOnly);
    saveConsent(necessaryOnly);
  };

  const savePreferences = () => {
    saveConsent(preferences);
    setShowSettings(false);
  };

  const cookieTypes = [
    {
      id: 'necessary',
      label: t('cookies.necessary'),
      description: t('cookies.necessaryDesc'),
      required: true
    },
    {
      id: 'functional',
      label: t('cookies.functional'),
      description: t('cookies.functionalDesc'),
      required: false
    },
    {
      id: 'analytics',
      label: t('cookies.analytics'),
      description: t('cookies.analyticsDesc'),
      required: false
    },
    {
      id: 'marketing',
      label: t('cookies.marketing'),
      description: t('cookies.marketingDesc'),
      required: false
    }
  ];

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="fixed bottom-0 left-0 right-0 z-[9999] p-4 md:p-6"
          data-testid="cookie-consent-banner"
        >
          <div className="max-w-6xl mx-auto bg-navy-900 rounded-2xl shadow-2xl border border-navy-700 overflow-hidden" role="dialog" aria-labelledby="cookie-banner-title" aria-modal="true">
            {!showSettings ? (
              <div className="p-6 md:p-8">
                <div className="flex flex-col md:flex-row md:items-center gap-6">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 bg-gold/20 rounded-full flex items-center justify-center">
                        <Cookie className="w-5 h-5 text-gold" aria-hidden="true" />
                      </div>
                      <h3 id="cookie-banner-title" className="text-lg font-outfit font-bold text-white">
                        {t('cookies.title')}
                      </h3>
                    </div>
                    <p className="text-steel-300 text-sm leading-relaxed">
                      {t('cookies.description')}
                      {' '}
                      <Link to="/cookies" className="text-gold hover:underline">
                        {t('cookies.learnMore')}
                      </Link>
                    </p>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button
                      variant="outline"
                      onClick={() => setShowSettings(true)}
                      className="border-steel-500 text-steel-300 hover:bg-steel-800 hover:text-white"
                      data-testid="cookie-settings-btn"
                      aria-label={t('cookies.settings')}
                    >
                      <Settings className="w-4 h-4 mr-2" aria-hidden="true" />
                      {t('cookies.settings')}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={acceptNecessary}
                      className="border-steel-500 text-steel-300 hover:bg-steel-800 hover:text-white"
                      data-testid="cookie-necessary-btn"
                    >
                      {t('cookies.necessaryOnly')}
                    </Button>
                    <Button
                      onClick={acceptAll}
                      className="bg-gold hover:bg-gold/90 text-navy-900 font-semibold"
                      data-testid="cookie-accept-all-btn"
                    >
                      <Check className="w-4 h-4 mr-2" aria-hidden="true" />
                      {t('cookies.acceptAll')}
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="p-6 md:p-8">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-outfit font-bold text-white">
                    {t('cookies.settingsTitle')}
                  </h3>
                  <button
                    onClick={() => setShowSettings(false)}
                    className="text-steel-400 hover:text-white transition-colors"
                    aria-label="Close settings"
                  >
                    <X className="w-5 h-5" aria-hidden="true" />
                  </button>
                </div>

                <div className="space-y-4 mb-6">
                  {cookieTypes.map((cookie) => (
                    <div
                      key={cookie.id}
                      className="flex items-start gap-4 p-4 bg-navy-800 rounded-lg"
                    >
                      <div className="pt-1">
                        <input
                          type="checkbox"
                          id={cookie.id}
                          checked={preferences[cookie.id]}
                          disabled={cookie.required}
                          onChange={(e) => 
                            setPreferences(prev => ({
                              ...prev,
                              [cookie.id]: e.target.checked
                            }))
                          }
                          className="w-5 h-5 rounded border-steel-500 bg-navy-700 text-gold focus:ring-gold disabled:opacity-50"
                        />
                      </div>
                      <div className="flex-1">
                        <label htmlFor={cookie.id} className="text-white font-medium cursor-pointer">
                          {cookie.label}
                          {cookie.required && (
                            <span className="ml-2 text-xs text-gold">
                              ({t('cookies.required')})
                            </span>
                          )}
                        </label>
                        <p className="text-sm text-steel-400 mt-1">{cookie.description}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="flex flex-col sm:flex-row gap-3 justify-end">
                  <Button
                    variant="outline"
                    onClick={() => setShowSettings(false)}
                    className="border-steel-500 text-steel-300 hover:bg-steel-800"
                  >
                    {t('common.cancel')}
                  </Button>
                  <Button
                    onClick={savePreferences}
                    className="bg-gold hover:bg-gold/90 text-navy-900 font-semibold"
                    data-testid="cookie-save-preferences-btn"
                  >
                    {t('cookies.savePreferences')}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieConsent;
